import { store } from '@/store';
import { getChatApiUrl } from '@/api/config';

export const IMAGE_TASK_POLL_INTERVAL = 3000;
export const IMAGE_TASK_TIMEOUT = 10 * 60 * 1000;

const activeTasks = new Map();

function findModel(task) {
  const models = store.settings && Array.isArray(store.settings.models) ? store.settings.models : [];
  return models.find(model => task.modelId && model.id === task.modelId)
    || models.find(model => model.endpoint === task.generationEndpoint && model.model === task.model)
    || null;
}

function normalizeStatus(status) {
  const value = String(status || '').toLowerCase();
  if (['completed', 'complete', 'succeeded', 'success', 'done'].includes(value)) return 'completed';
  if (['failed', 'error', 'cancelled', 'canceled'].includes(value)) return 'failed';
  if (value === 'queued' || value === 'pending') return 'queued';
  return 'in_progress';
}

function getTaskApiUrl(task) {
  const chatApiUrl = getChatApiUrl(task.generationEndpoint || '', false);
  return `${chatApiUrl}?action=image_task`;
}

async function queryTask(task, signal) {
  const model = findModel(task);
  const apiKey = model && model.apiKey ? model.apiKey : '';
  if (!task.taskId || !task.generationEndpoint || !apiKey) {
    throw new Error('无法恢复绘图任务：模型配置已不存在或缺少 API Key。');
  }

  const response = await fetch(getTaskApiUrl(task), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      task_id: task.taskId,
      generation_endpoint: task.generationEndpoint,
      task_endpoint: task.taskEndpoint || '',
      api_key: apiKey,
      provider: task.provider || 'openai',
      model: task.model || ''
    }),
    signal
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok || (payload.code && payload.code >= 400)) {
    const message = payload.error && payload.error.message || payload.message || `任务查询失败（${response.status}）`;
    throw new Error(message);
  }

  return {
    ...payload,
    status: normalizeStatus(payload.status),
    progress: payload.progress == null ? null : Number(payload.progress)
  };
}

export function stopImageTask(taskId) {
  const active = activeTasks.get(taskId);
  if (!active) return;
  active.controller.abort();
  activeTasks.delete(taskId);
}

export function stopAllImageTasks() {
  Array.from(activeTasks.keys()).forEach(stopImageTask);
}

export function pollImageTask(task, callbacks = {}) {
  if (!task || !task.taskId) return Promise.reject(new Error('Missing image task id'));
  const existing = activeTasks.get(task.taskId);
  if (existing) return existing.promise;

  const controller = new AbortController();
  const startedAt = Date.now();
  const state = { controller, promise: null };

  const run = async () => {
    let current = { ...task };
    try {
      while (true) {
        if (Date.now() - startedAt >= IMAGE_TASK_TIMEOUT) {
          throw Object.assign(new Error('绘图任务轮询超时（10 分钟）。'), { code: 'IMAGE_TASK_TIMEOUT' });
        }

        const result = await queryTask(current, controller.signal);
        current = { ...current, status: result.status, progress: result.progress, updatedAt: Date.now() };
        if (callbacks.onUpdate) callbacks.onUpdate(current, result);

        if (result.status === 'completed') {
          if (callbacks.onComplete) callbacks.onComplete(current, result);
          return result;
        }
        if (result.status === 'failed') {
          const error = result.error && result.error.message || '绘图任务失败。';
          throw new Error(error);
        }

        await new Promise((resolve, reject) => {
          const timer = setTimeout(resolve, IMAGE_TASK_POLL_INTERVAL);
          controller.signal.addEventListener('abort', () => {
            clearTimeout(timer);
            reject(Object.assign(new Error('Image task polling aborted'), { name: 'AbortError' }));
          }, { once: true });
        });
      }
    } catch (error) {
      if (callbacks.onError && error.name !== 'AbortError') callbacks.onError(error, current);
      throw error;
    } finally {
      if (activeTasks.get(current.taskId) === state) activeTasks.delete(current.taskId);
    }
  };

  state.promise = run();
  activeTasks.set(task.taskId, state);
  return state.promise;
}

export function resumeImageTasks(messages = [], callbacks = {}) {
  const promises = [];
  (Array.isArray(messages) ? messages : []).forEach(message => {
    const task = message && message.imageTask;
    if (!task || !task.taskId || ['completed', 'failed', 'timeout'].includes(task.status)) return;
    promises.push(pollImageTask(task, {
      onUpdate: (next, result) => callbacks.onUpdate && callbacks.onUpdate(message, next, result),
      onComplete: (next, result) => callbacks.onComplete && callbacks.onComplete(message, next, result),
      onError: (error, next) => callbacks.onError && callbacks.onError(message, error, next)
    }).catch(() => null));
  });
  return Promise.all(promises);
}
